import { Suspense } from "react";
import { EmptyState } from "@/components/elements/EmptyState";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { ConnectionBridgeBaseFragment } from "@/apollo/types";
import { reverse, sortBy } from "lodash";
import { Button } from "@/components/elements/Button";
import { PlusIcon } from "@heroicons/react/24/outline";
import { AuthorizationLinkFeatureGuard } from "./components/AuthorizationLinkFeatureGuard";
import { AuthorizationLinkTeaser } from "./components/AuthorizationLinkTeaser";
import { AuthorizationLinksTable } from "@/pages/Connections/AuthorizationLinksTable";
import { ApolloError } from "@apollo/client";

export function AuthorizationLinksTab(props: {
  connectionBridges: ConnectionBridgeBaseFragment[];
  error?: ApolloError;
  loading?: boolean;
  onCreateAuthorizationLink?: () => void;
  onDeleted?: () => void;
}) {
  // Sort connection bridges by createdAt in descending order
  const sortedConnectionBridges = reverse(
    sortBy(props.connectionBridges, "createdAt"),
  );

  if (props.error) {
    return <EmptyState header="Error" description={props.error.message} />;
  }

  return (
    <AuthorizationLinkFeatureGuard
      fallback={() => (
        <div className="mx-auto w-full max-w-lg rounded border border-gray-200 p-8 dark:border-gray-700">
          <AuthorizationLinkTeaser />
        </div>
      )}
    >
      {!sortedConnectionBridges.length ? (
        <EmptyState
          header="No Authorization Links Found"
          description="Authorization Links enable to share a secure link with someone else to authorize a connection outside of your Weld account. This is useful when you don’t have direct access to the required account or data source."
          action={
            <Button
              onClick={props.onCreateAuthorizationLink}
              icon={<PlusIcon />}
            >
              New Authorization Link
            </Button>
          }
        />
      ) : (
        <Suspense fallback={<LoadingSpinner />}>
          <AuthorizationLinksTable
            data={sortedConnectionBridges}
            loading={props.loading}
            onDeleted={props.onDeleted}
          />
        </Suspense>
      )}
    </AuthorizationLinkFeatureGuard>
  );
}
